import React, { useState } from "react";
import {
    FieldArray,
    FieldArrayPath,
    FieldValues,
    Path,
    useFieldArray,
    useFormContext,
    useWatch,
} from "react-hook-form";
import { FieldConfig } from "../../../../../models/Models";
import FormFieldsRenderer from "./FormFieldsRenderer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";


interface ArrayFieldsRendererProps<T extends FieldValues, TName extends FieldArrayPath<T>> {
    label: string;
    name: TName;
    config?: FieldConfig<T>[];
    areItemsRemovable?: boolean;
    isAppendable?: boolean;
}

const ArrayFieldsRenderer = <T extends FieldValues, TName extends FieldArrayPath<T>> ({
    label,
    name,
    config,
    areItemsRemovable = true,
    isAppendable = true,
 }: ArrayFieldsRendererProps<T, TName>) => {
    const { control } = useFormContext<T>();
    const { fields, append, remove } = useFieldArray({
        control,
        name,
    });
    const [ value, setValue ] = useState("");
    const watchedValues = useWatch({ name: name as Path<T>, control });

    const addItemToList = () => {
        if (!value.trim()) return;

        const newValue = value.trim();
        const newValuesExists = watchedValues.includes(newValue);

        if(newValuesExists) return;

        append(value.trim() as FieldArray<T, TName>);
        setValue("");
    };

    if (config)
        return (
            <>
                <div className={`flex flex-col gap-2`}>
                    {
                        fields.map((field, i) => {
                            const itemBasePath = `${name}.${i}`;

                            return (
                                <div
                                    key={field.id}
                                    className={`p-2 border-brand-500/10 dark:border-white/10 rounded-md border`}
                                >
                                    <label
                                        className={`dark:text-gray-100 mb-1`}
                                    >
                                        {label}
                                    </label>
                                    <div className={`px-2 pt-2 relative`}>
                                        <FormFieldsRenderer
                                            config={config}
                                            basePath={itemBasePath}
                                        />
                                        {
                                            areItemsRemovable &&
                                            <button
                                                type="button"
                                                className={`top-1 right-2 border-brand-500/10 dark:border-brand-300/10 rounded-lg p-1 absolute border`}
                                                onClick={() => remove(i)}
                                            >
                                                remove
                                            </button>

                                        }
                                    </div>
                                </div>
                            );
                        })
                    }
                    {
                        isAppendable &&
                        <div className={`flex justify-end`}>
                            <button type="button" className={`text-white bg-blue-800 dark:bg-brand-300 rounded-md shadow-xl p-2 w-fit`} onClick={() => append({} as FieldArray<T, TName>)}>
                                {`Add more ${label}`}
                            </button>
                        </div>

                    }
                </div>
            </>
        );

    return (
        <div  className={`py-2 flex flex-col gap-2`}>
            <label className = {`dark:text-gray-100 mb-1`}>{label}</label>
            <div className={`flex gap-2 items-center`}>
                <input
                    onChange={(e) => {
                        setValue(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                            e.preventDefault();
                            e.stopPropagation();
                            addItemToList();
                        }
                    }}
                    value={value}
                    type = {"text"}
                    className = {`bg-gray-300 dark:bg-brand-900 w-full p-2 rounded-md`}
                />
                <button
                    type="button"
                    className = {`flex flex-row items-center gap-2 bg-blue-800 dark:bg-brand-300 rounded-md shadow-xl p-2`}
                    onClick={() => {
                        addItemToList();
                    }}
                >
                    <FontAwesomeIcon icon = {faPlus} size = "1x" className = {`text-white`} />
                </button>
            </div>
            <div className = {`flex flex-wrap gap-1`}>
                {
                    watchedValues && watchedValues.map((watchedValue: string, index: number) => {
                        return (
                            <div
                                key = {watchedValue}
                                className = {`flex flex-row items-center gap-1 py-1 px-2 text-xs text-brand-900 bg-brand-300 dark:text-white dark:bg-brand-500 font-bold rounded-full`}
                            >
                                <p>{watchedValue}</p>
                                <button type="button" onClick={() => remove(index)}>
                                    <FontAwesomeIcon icon = {faXmark} size = "1x" />
                                </button>
                            </div>
                        );
                    })
                }
            </div>
        </div>
    );
};

export default ArrayFieldsRenderer;